import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { AdminFeedbackDetail } from "@shared-core/data/api-types";
import { formatLongDateOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";

export function SettingsFeedbackDetailPage() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const [detail, setDetail] = useState<AdminFeedbackDetail | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!feedbackId) return;
    let cancelled = false;
    adapter
      .getAdminFeedbackDetail(feedbackId)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, feedbackId]);

  if (error) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(paths.settingsFeedback())}
        >
          <ArrowLeft className="size-4 mr-2" />
          Back to feedback list
        </Button>
        <p className="text-muted-foreground">Feedback not found.</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(paths.settingsFeedback())}
        >
          <ArrowLeft className="size-4 mr-2" />
          Back to feedback list
        </Button>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const date = formatLongDateOrDash(detail.created_at);

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(paths.settingsFeedback())}
      >
        <ArrowLeft className="size-4 mr-2" />
        Back to feedback list
      </Button>

      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">
            {detail.summary_headline ?? "Feedback"}
          </h2>
          {detail.mode === "brain_sucker" && (
            <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              Brain Sucker
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {detail.user_email ?? detail.user_id} &middot; {date}
          {detail.page_path && <> &middot; {detail.page_path}</>}
        </p>
      </div>

      {/* AI Summary */}
      {detail.summary_text && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">AI Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm whitespace-pre-wrap">
              {detail.summary_text}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conversation thread */}
      {detail.conversation_thread && detail.conversation_thread.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-3">
            Conversation ({detail.message_count} messages)
          </h3>
          <div className="border rounded-lg p-4 space-y-3 max-h-[60vh] overflow-y-auto bg-muted/30">
            {detail.conversation_thread.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className="max-w-[80%]">
                  <div className="text-xs text-muted-foreground mb-1">
                    {msg.role === "user"
                      ? (detail.user_email ?? detail.user_id)
                      : "AI"}
                  </div>
                  <div
                    className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-background border"
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
